// Opciones y etiquetas del formulario de promo de venta.
import type {
    AccessorySubcategory,
    CouponScope,
    DiscountType,
    GiftMode,
    PromotionPaymentOption,
} from '../api/promotionsContract';

export { PRODUCT_CATEGORY_OPTIONS, PRODUCT_TIER_OPTIONS } from '@/shared/domain/productScope';

export const DISCOUNT_TYPE_OPTIONS: { value: DiscountType; label: string }[] = [
    { value: 'AMOUNT', label: '$ fijo' },
    { value: 'PERCENTAGE', label: '% off' },
];

export const GIFT_MODE_OPTIONS: { value: GiftMode; label: string }[] = [
    { value: 'LIST', label: 'De una lista' },
    { value: 'RANGE', label: 'De una gama' },
];

export const ACCESSORY_SUBCATEGORY_LABEL: Record<AccessorySubcategory, string> = {
    AUDIO: 'Audio',
    CHARGING: 'Carga',
    PROTECTION: 'Protección (fundas y micas)',
    CABLES: 'Cables',
    WEARABLES: 'Wearables',
    OTHER: 'Otros',
};

export const ACCESSORY_SUBCATEGORY_OPTIONS: { value: AccessorySubcategory; label: string }[] =
    Object.entries(ACCESSORY_SUBCATEGORY_LABEL).map(([value, label]) => ({
        value: value as AccessorySubcategory,
        label,
    }));

export const COUPON_SCOPE_OPTIONS: { value: CouponScope; label: string }[] = [
    { value: 'CHAIN', label: 'Vale en toda la cadena' },
    { value: 'ISSUING_STORE', label: 'Solo en la tienda donde compró' },
    { value: 'PROMOTION_STORES', label: 'Solo en las tiendas del alcance' },
];

interface PaymentOptionMeta {
    value: PromotionPaymentOption;
    /** Texto del chip. */
    label: string;
    /** Cómo se nombra en el resumen «Aplica con …». */
    phrase: string;
}

/** Las 8 formas de pago en el orden del catálogo. */
export const PAYMENT_OPTION_VALUES = [
    'SUREKEEP',
    'PAYJOY',
    'CREDIYA',
    'LESPAGO',
    'INNOVA',
    'CASH',
    'CARD',
    'WIRE',
] as const satisfies readonly PromotionPaymentOption[];

/** Chips de forma de pago: financieras y luego medios de contado, en el orden del catálogo. */
export const PAYMENT_OPTION_GROUPS: readonly (readonly PaymentOptionMeta[])[] = [
    [
        { value: 'SUREKEEP', label: 'SureKeep', phrase: 'crédito SureKeep' },
        { value: 'PAYJOY', label: 'PayJoy', phrase: 'PayJoy' },
        { value: 'CREDIYA', label: 'CrediYa', phrase: 'CrediYa' },
        { value: 'LESPAGO', label: 'LesPago', phrase: 'LesPago' },
        { value: 'INNOVA', label: 'Innova', phrase: 'Innova' },
    ],
    [
        { value: 'CASH', label: 'Efectivo', phrase: 'pago en efectivo' },
        { value: 'CARD', label: 'Tarjeta', phrase: 'pago con tarjeta' },
        { value: 'WIRE', label: 'Transferencia', phrase: 'transferencia' },
    ],
];

/** «crédito SureKeep o pago en efectivo»; `null` si no hay nada marcado. */
export function paymentOptionsPhrase(selected: readonly PromotionPaymentOption[]): string | null {
    const phrases = PAYMENT_OPTION_GROUPS.flat()
        .filter((option) => selected.includes(option.value))
        .map((option) => option.phrase);
    return phrases.length === 0 ? null : joinWithOr(phrases);
}

/** «Aplica con crédito SureKeep o pago en efectivo»; sin nada marcado, con cualquier forma de pago. */
export function paymentOptionsSummary(selected: readonly PromotionPaymentOption[]): string {
    return `Aplica con ${paymentOptionsPhrase(selected) ?? 'cualquier forma de pago'}`;
}

// «a, b o c»; una sola va tal cual.
function joinWithOr(items: string[]): string {
    const last = items.pop() ?? '';
    return items.length > 0 ? `${items.join(', ')} o ${last}` : last;
}

/** «SureKeep o efectivo»: la marca de cada financiera y el medio de contado en minúscula. */
export function paymentOptionsShortText(selected: readonly PromotionPaymentOption[]): string {
    const [financiers = [], cash = []] = PAYMENT_OPTION_GROUPS;
    const labels = [
        ...financiers.filter((o) => selected.includes(o.value)).map((o) => o.label),
        ...cash.filter((o) => selected.includes(o.value)).map((o) => o.label.toLowerCase()),
    ];
    return joinWithOr(labels);
}
